import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import {
  ConfirmDialogConfig,
  ConfirmationDialogComponent,
  ConfirmationDialogComponentModule,
} from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'ts-json-editor',
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    ConfirmationDialogComponentModule,
  ],
})
export class JsonEditorComponent implements OnChanges {
  @Input() value: unknown;
  @Input() titleKey = '';
  @Input() descriptionKey = '';
  @Input() confirmWarningKey = '';
  @Input() discardWarningKey = '';
  @Input() confirmButtonKey = 'button.confirm';
  @Input() discardButtonKey = 'button.discard_changes';
  @Input() indent = 4;
  @Input() showHeaderActions = true;

  @Output() confirmed = new EventEmitter<unknown>();
  @Output() discarded = new EventEmitter<void>();
  @Output() parseStateChange = new EventEmitter<{
    value: unknown | null;
    valid: boolean;
  }>();

  @ViewChild('editor') private readonly editorRef?: ElementRef<HTMLTextAreaElement>;

  editorText = '';
  parseError: string | null = null;
  private hasSeeded = false;

  constructor(private readonly dialog: MatDialog, private readonly cdr: ChangeDetectorRef) {}

  // `value` is a one-time seed, not a controlled input. After the first
  // non-undefined seed the textarea is the source of truth; later `value`
  // changes (typically echoes from a parent that re-emits the parsed result)
  // are ignored so the user's in-progress typing is never overwritten.
  // To re-seed, the consumer must destroy and recreate the component.
  ngOnChanges(changes: SimpleChanges): void {
    const valueChanged = 'value' in changes;
    const indentChanged = 'indent' in changes;
    if (!valueChanged && !indentChanged) return;
    if (this.hasSeeded) return;
    if (valueChanged && this.value === undefined) return;

    this.editorText = this.serialize(this.value);
    this.parseError = null;
    this.emitParseState();
    if (valueChanged) this.hasSeeded = true;
  }

  onTextChange(text: string): void {
    this.editorText = text;
    this.parseError = this.validate(text);
    this.emitParseState();
  }

  onKeydown(event: KeyboardEvent): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (event.key === 'Tab') {
      event.preventDefault();
      if (event.shiftKey) {
        this.outdent(textarea);
      } else {
        this.indentSelection(textarea);
      }
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.newlineWithIndent(textarea);
      return;
    }
    if (event.key === 'Backspace') {
      if (this.tryOutdentBackspace(textarea)) {
        event.preventDefault();
      }
    }
  }

  onConfirmClick(): void {
    if (this.parseError) return;
    const parsed = JSON.parse(this.editorText);
    this.openWarningDialog(this.confirmWarningKey, this.confirmButtonKey).subscribe((answer) => {
      if (answer === 'accept') this.confirmed.emit(parsed);
    });
  }

  onDiscardClick(): void {
    this.openWarningDialog(this.discardWarningKey, this.discardButtonKey).subscribe((answer) => {
      if (answer === 'accept') this.discarded.emit();
    });
  }

  private openWarningDialog(textKey: string, acceptKey: string) {
    const data: ConfirmDialogConfig = {
      text: textKey,
      acceptButtonText: acceptKey,
      cancelButtonText: 'button.cancel',
      warningDialog: true,
      defaultAction: 'cancel',
    };
    return this.dialog
      .open(ConfirmationDialogComponent, { data })
      .afterClosed()
      .pipe(take(1));
  }

  private serialize(value: unknown): string {
    if (value === undefined) return '';
    try {
      return JSON.stringify(value, null, this.indent);
    } catch {
      return '';
    }
  }

  private validate(text: string): string | null {
    if (text.trim().length === 0) return null;
    try {
      JSON.parse(text);
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  }

  private indentSelection(textarea: HTMLTextAreaElement): void {
    const { selectionStart, selectionEnd, value } = textarea;
    const pad = ' '.repeat(this.indent);

    if (selectionStart === selectionEnd) {
      const next = value.slice(0, selectionStart) + pad + value.slice(selectionEnd);
      this.applyEdit(textarea, next, selectionStart + pad.length, selectionStart + pad.length);
      return;
    }

    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const before = value.slice(0, lineStart);
    const block = value.slice(lineStart, selectionEnd);
    const after = value.slice(selectionEnd);
    const indented = block.replace(/^/gm, pad);
    const addedBeforeStart = selectionStart === lineStart ? 0 : pad.length;
    const addedTotal = pad.length * (block.match(/\n/g)?.length ?? 0) + pad.length;
    this.applyEdit(
      textarea,
      before + indented + after,
      selectionStart + addedBeforeStart,
      selectionEnd + addedTotal
    );
  }

  private outdent(textarea: HTMLTextAreaElement): void {
    const { selectionStart, selectionEnd, value } = textarea;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const before = value.slice(0, lineStart);
    const block = value.slice(lineStart, selectionEnd);
    const after = value.slice(selectionEnd);

    let removedFirst = 0;
    let removedTotal = 0;
    const stripped = block
      .split('\n')
      .map((line, idx) => {
        const match = line.match(/^ {1,}/);
        if (!match) return line;
        const remove = Math.min(this.indent, match[0].length);
        if (idx === 0) removedFirst = remove;
        removedTotal += remove;
        return line.slice(remove);
      })
      .join('\n');

    const newStart = Math.max(lineStart, selectionStart - removedFirst);
    const newEnd = Math.max(newStart, selectionEnd - removedTotal);
    this.applyEdit(textarea, before + stripped + after, newStart, newEnd);
  }

  private newlineWithIndent(textarea: HTMLTextAreaElement): void {
    const { selectionStart, selectionEnd, value } = textarea;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const currentLine = value.slice(lineStart, selectionStart);
    const leading = currentLine.match(/^[ \t]*/)?.[0] ?? '';
    const prevChar = value.slice(0, selectionStart).replace(/[ \t]*$/, '').slice(-1);
    const deeper = prevChar === '{' || prevChar === '[';
    const extra = deeper ? ' '.repeat(this.indent) : '';
    const insert = '\n' + leading + extra;
    const next = value.slice(0, selectionStart) + insert + value.slice(selectionEnd);
    const caret = selectionStart + insert.length;
    this.applyEdit(textarea, next, caret, caret);
  }

  private tryOutdentBackspace(textarea: HTMLTextAreaElement): boolean {
    const { selectionStart, selectionEnd, value } = textarea;
    if (selectionStart !== selectionEnd) return false;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const before = value.slice(lineStart, selectionStart);
    if (before.length === 0 || !/^ +$/.test(before)) return false;
    if (before.length % this.indent !== 0) return false;
    const remove = this.indent;
    const next = value.slice(0, selectionStart - remove) + value.slice(selectionStart);
    const caret = selectionStart - remove;
    this.applyEdit(textarea, next, caret, caret);
    return true;
  }

  private applyEdit(textarea: HTMLTextAreaElement, next: string, start: number, end: number): void {
    textarea.value = next;
    this.editorText = next;
    textarea.setSelectionRange(start, end);
    this.parseError = this.validate(next);
    this.emitParseState();
    this.cdr.markForCheck();
  }

  private emitParseState(): void {
    const result = this.tryParseObject(this.editorText);
    this.parseStateChange.emit(result);
  }

  private tryParseObject(text: string): { value: unknown | null; valid: boolean } {
    if (this.parseError) return { value: null, valid: false };
    const trimmed = text.trim();
    if (trimmed.length === 0) return { value: null, valid: false };
    try {
      const parsed = JSON.parse(trimmed);
      const isPlainObject =
        parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
      return isPlainObject ? { value: parsed, valid: true } : { value: null, valid: false };
    } catch {
      return { value: null, valid: false };
    }
  }
}
